import React, { Component } from 'react';
import moment from 'moment';
import API from '../../utils/API';
import SaveBar from './components/SaveBar';

/* Match the wizard exactly */
const ROOM_TYPES = [
	'Meeting', 'Breakout', 'General Session', 'Exhibit Hall',
	'Pre-function', 'Ballroom', 'Board Room', 'Other'
];

const SETUP_PREFERENCES = [
	'No preference', 'Buffet', 'Cabaret', 'Chevron', 'Classroom', 'Conference',
	'Crescent Rounds', 'Flow', 'Hollow Square', 'Island', 'Peninsula', 'Plated',
	'Reception', 'Rounds', 'Rounds of 6', 'Rounds of 8', 'Rounds of 10',
	'Tabletop exhibit', 'Theater', 'T-Shaped', 'U-Shaped', 'Other'
];

const BLANK_ITEM = {
	roomType: 'Meeting',
	startTime: '8:00 AM',
	endTime: '9:00 AM',
	attendees: '',
	selectedDates: [],
	require24Hour: false,
	setupPreference: [],
	sessionName: '',
	roomSize: '',
	roomSizeUnit: 'sq. ft.',
	copiesPerDay: 1,
	notes: ''
};

class MeetingSpaces extends Component {
	constructor(props) {
		super(props);
		/* Stamp a stable client-side id on every item that doesn't have one
		   so that checkedItems keying never collapses multiple rows together. */
		const agendaItems = (props.request?.agendaItems || []).map((it, i) => ({
			...it,
			id: it.id || it._id || `loaded-${i}-${Date.now()}`
		}));
		this.state = {
			items: agendaItems,
			showAddModal: false,
			draft: { ...BLANK_ITEM },
			showAdditional: false,
			checkedItems: {},
			editing: false,
			saving: false,
		};
	}

	/* ── Modal helpers ──────────────────────────────────── */
	openModal = () => this.setState({
		showAddModal: true,
		draft: { ...BLANK_ITEM },
		showAdditional: false,
	});
	closeModal = () => this.setState({
		showAddModal: false,
	});

	/* ── Draft form helpers ─────────────────────────────── */
	updateDraft = (field, value) => {
		this.setState((s) => ({
			draft: { ...s.draft, [field]: value },
		}));
	};

	toggleDraftDate = (date) => {
		this.setState((s) => {
			const dates = [...s.draft.selectedDates];
			const idx = dates.indexOf(date);
			if (idx >= 0) dates.splice(idx, 1); else dates.push(date);
			return {
				draft: { ...s.draft, selectedDates: dates },
			};
		});
	};

	toggleDraftSetup = (pref) => {
		this.setState((s) => {
			const prefs = [...s.draft.setupPreference];
			const idx = prefs.indexOf(pref);
			if (idx >= 0) prefs.splice(idx, 1); else prefs.push(pref);
			return { draft: { ...s.draft, setupPreference: prefs } };
		});
	};

	publishToAgenda = () => {
		const { draft } = this.state;

		this.setState((s) => ({
			items: [...s.items, { ...s.draft, id: Date.now() }],
			showAddModal: false,
			draft: { ...BLANK_ITEM },
			showAdditional: false,
			editing: true,
		}));
	};

	/* ── Agenda list helpers ───────────────────────────── */
	toggleCheck = (id) => {
		this.setState((s) => {
			const c = { ...s.checkedItems };
			c[id] ? delete c[id] : (c[id] = true);
			return { checkedItems: c };
		});
	};

	deleteChecked = () => {
		this.setState((s) => ({
			items: s.items.filter((it) => !s.checkedItems[it.id]),
			checkedItems: {},
			editing: true
		}));
	};

	/* ── Persistence ───────────────────────────────────── */
	save = async () => {
		this.setState({ saving: true });
		try {
			await API().patch(
				`Organizations/${this.props.orgId}/proposal-requests/${this.props.requestId}`,
				{ agendaItems: this.state.items }
			);
			this.setState({ editing: false, saving: false });
			this.props.refreshSectionCounts && this.props.refreshSectionCounts();
		} catch (e) {
			console.error('Error saving agenda:', e);
			this.setState({ saving: false });
		}
	};

	cancel = () => {
		this.setState({
			items: this.props.request?.agendaItems || [],
			draft: { ...BLANK_ITEM },
			checkedItems: {},
			editing: false
		});
	};

	/* ── Date helpers ──────────────────────────────────── */
	getAvailableDates = () => {
		const { request } = this.props;
		const arrival = request?.eventDetails?.arrivalDate;
		const departure = request?.eventDetails?.departureDate;
		if (arrival && departure) {
			const dates = [];
			let cur = moment(arrival);
			const end = moment(departure);
			while (cur <= end) {
				dates.push(cur.format('MMM D'));
				cur.add(1, 'days');
			}
			return dates;
		}
		return [];
	};

	groupByDate = () => {
		const { items } = this.state;
		const grouped = {};

		const toMinutes = (time) => {
			const parsed = moment(time, ['h:mm A', 'HH:mm'], true);
			if (!parsed.isValid()) return Number.MAX_SAFE_INTEGER;
			return (parsed.hours() * 60) + parsed.minutes();
		};

		const getDurationMinutes = (item) => {
			const start = toMinutes(item.startTime);
			const end = toMinutes(item.endTime);
			if (start === Number.MAX_SAFE_INTEGER || end === Number.MAX_SAFE_INTEGER) {
				return Number.MAX_SAFE_INTEGER;
			}
			if (end >= start) return end - start;
			return (24 * 60 - start) + end;
		};

		items.forEach((item) => {
			const dates = item.selectedDates || [];
			if (dates.length === 0) {
				if (!grouped['Unassigned']) grouped['Unassigned'] = [];
				grouped['Unassigned'].push(item);
			} else {
				dates.forEach((d) => {
					if (!grouped[d]) grouped[d] = [];
					grouped[d].push(item);
				});
			}
		});

		Object.keys(grouped).forEach((dateKey) => {
			grouped[dateKey].sort((a, b) => {
				const startDiff = toMinutes(a.startTime) - toMinutes(b.startTime);
				if (startDiff !== 0) return startDiff;

				const durationDiff = getDurationMinutes(a) - getDurationMinutes(b);
				if (durationDiff !== 0) return durationDiff;

				const nameA = (a.sessionName || a.roomType || '').toLowerCase();
				const nameB = (b.sessionName || b.roomType || '').toLowerCase();
				return nameA.localeCompare(nameB);
			});
		});

		return grouped;
	};

	render() {
		const { items, showAddModal, draft, showAdditional, checkedItems, editing, saving } = this.state;
		const { request } = this.props;

		const availableDates = this.getAvailableDates();
		const grouped = this.groupByDate();
		const baseYear = request?.eventDetails?.arrivalDate ? moment(request.eventDetails.arrivalDate).year() : moment().year();
		const groupedEntries = Object.entries(grouped).sort(([dateA], [dateB]) => {
			const parsedA = moment(`${dateA} ${baseYear}`, 'MMM D YYYY', true);
			const parsedB = moment(`${dateB} ${baseYear}`, 'MMM D YYYY', true);

			if (!parsedA.isValid() && !parsedB.isValid()) return dateA.localeCompare(dateB);
			if (!parsedA.isValid()) return 1;
			if (!parsedB.isValid()) return -1;
			return parsedA.valueOf() - parsedB.valueOf();
		});
		const checkedCount = Object.keys(checkedItems).length;

		return (
			<>
				{/* ── Agenda header + add button ───────────────────── */}
				<div className="pr-subsection-head" style={{ marginTop: 0 }}>
					<h2 className="pr-subsection-title" style={{ marginTop: 0 }}>
						Agenda
						{items.length > 0 && (
							<span className="pr-save-bar-meta" style={{ marginLeft: 10 }}>
								{items.length} item{items.length !== 1 ? 's' : ''}
							</span>
						)}
					</h2>
					<button type="button" className="pr-btn-ghost" onClick={this.openModal}>
						<i className="las la-plus" />
						Add item
					</button>
				</div>

				{/* ── Agenda list ──────────────────────────────────── */}
				{items.length === 0 ? (
					<div className="pr-section-empty">
						<i className="las la-couch" />
						No agenda items yet. Click "Add item" to add rooms, sessions and meals.
					</div>
				) : (
					<>
						{checkedCount > 0 && (
							<div className="pr-ms-batch-bar">
								<button
									type="button"
									className="pr-icon-btn"
									onClick={() => this.setState({ checkedItems: {} })}
								>
									<i className="las la-times" />
								</button>
								<span style={{ fontSize: 13 }}>{checkedCount} selected</span>
								<button
									type="button"
									className="pr-ms-batch-btn danger"
									onClick={this.deleteChecked}
								>
									Delete
								</button>
							</div>
						)}

						{groupedEntries.map(([date, dateItems]) => (
							<div key={date} className="pr-ms-date-group">
								<div className="pr-ms-date-header">{date}</div>
								{dateItems.map((item) => (
									<div
										key={item.id}
										className={`pr-ms-row ${checkedItems[item.id] ? 'checked' : ''}`}
									>
										<input
											type="checkbox"
											className="pr-ms-checkbox"
											checked={!!checkedItems[item.id]}
											onChange={() => this.toggleCheck(item.id)}
										/>
										<div className="pr-ms-time">{item.startTime} – {item.endTime}</div>
										<div className="pr-ms-room-type">{item.roomType}</div>
										{item.require24Hour && (
											<span className="pr-ms-badge">
												<i className="las la-redo-alt" /> 24hr
											</span>
										)}
										{item.attendees > 0 && (
											<span className="pr-ms-meta">
												<i className="las la-user" /> {item.attendees}
											</span>
										)}
										{item.setupPreference?.length > 0 && (
											<span className="pr-ms-meta">
												<i className="las la-th" /> {item.setupPreference[0]}
											</span>
										)}
										{item.sessionName && (
											<span className="pr-ms-meta pr-ms-name">{item.sessionName}</span>
										)}
									</div>
								))}
							</div>
						))}
					</>
				)}

				{/* ── Add agenda item modal ────────────────────────── */}
				{showAddModal && (
					<>
						<div className="pr-modal-backdrop" onClick={this.closeModal} />
						<div className="pr-modal">
							<div className="pr-modal-header">
								<h3 className="pr-modal-title">Add Agenda Item</h3>
								<button type="button" className="pr-overlay-close" onClick={this.closeModal}>
									<i className="las la-times" />
								</button>
							</div>

							<div className="pr-modal-body">
								<div className="pr-ms-form-grid">
									<div className="pr-field-group compact">
										<label className="pr-field-label">Room Type</label>
										<div className="pr-select-wrap">
											<select
												className="pr-select"
												value={draft.roomType}
												onChange={(e) => this.updateDraft('roomType', e.target.value)}
											>
												{ROOM_TYPES.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
											</select>
											<i className="las la-angle-down pr-select-arrow" />
										</div>
									</div>

									<div className="pr-field-group compact">
										<label className="pr-field-label">Start Time</label>
										<input
											className="pr-text-input"
											type="time"
											value={draft.startTime ? moment(draft.startTime, 'h:mm A').format('HH:mm') : ''}
											onChange={(e) => this.updateDraft('startTime', moment(e.target.value, 'HH:mm').format('h:mm A'))}
										/>
									</div>

									<div className="pr-field-group compact">
										<label className="pr-field-label">End Time</label>
										<input
											className="pr-text-input"
											type="time"
											value={draft.endTime ? moment(draft.endTime, 'h:mm A').format('HH:mm') : ''}
											onChange={(e) => this.updateDraft('endTime', moment(e.target.value, 'HH:mm').format('h:mm A'))}
										/>
									</div>

									<div className="pr-field-group compact">
										<label className="pr-field-label">Attendees</label>
										<input
											className="pr-text-input"
											type="number"
											min="0"
											value={draft.attendees}
											onChange={(e) => this.updateDraft('attendees', e.target.value)}
											placeholder="0"
										/>
									</div>
								</div>

								{availableDates.length > 0 && (
									<div className="pr-field-group compact">
										<label className="pr-field-label">Select Dates</label>
										<div className="pr-day-selector">
											{availableDates.map((date) => {
												const on = draft.selectedDates.includes(date);
												return (
													<button
														key={date}
														type="button"
														className={`pr-day-btn ${on ? 'active' : ''}`}
														onClick={() => this.toggleDraftDate(date)}
													>
														{date}
													</button>
												);
											})}
										</div>
										</div>
									)}

								<div className="pr-field-group compact">
									<button
										type="button"
										className={`pr-toggle ${draft.require24Hour ? 'on' : ''}`}
										onClick={() => this.updateDraft('require24Hour', !draft.require24Hour)}
									>
										<span className="pr-toggle-track" />
										<span>Require 24-hour hold</span>
									</button>
								</div>

								<div className="pr-field-group compact">
									<label className="pr-field-label">Setup Preference</label>
									<div className="pr-ms-setup-chips">
										{SETUP_PREFERENCES.map((pref) => (
											<button
												key={pref}
												type="button"
												className={`pr-ms-chip ${draft.setupPreference.includes(pref) ? 'active' : ''}`}
												onClick={() => this.toggleDraftSetup(pref)}
											>
												{pref}
											</button>
										))}
									</div>
								</div>

								<button
									type="button"
									className="pr-ms-details-toggle"
									onClick={() => this.setState((s) => ({ showAdditional: !s.showAdditional }))}
								>
									<i className={`las la-angle-${showAdditional ? 'up' : 'down'}`} />
									{showAdditional ? 'Hide additional details' : 'View additional details'}
								</button>

								{showAdditional && (
									<div style={{ marginTop: 16 }}>
										<div className="pr-row-3">
											<div className="pr-field-group compact">
												<label className="pr-field-label">Session Name</label>
												<input
													className="pr-text-input"
													type="text"
													value={draft.sessionName}
													onChange={(e) => this.updateDraft('sessionName', e.target.value)}
													placeholder="Keynote, Lunch, Workshop…"
												/>
											</div>
											<div className="pr-field-group compact">
												<label className="pr-field-label">
													Room Size
													<span className="pr-field-hint">sq. ft.</span>
												</label>
												<input
													className="pr-text-input"
													type="number"
													min="0"
													value={draft.roomSize}
													onChange={(e) => this.updateDraft('roomSize', e.target.value)}
													placeholder="0"
												/>
											</div>
											<div className="pr-field-group compact">
												<label className="pr-field-label">Copies per day</label>
												<input
													className="pr-text-input"
													type="number"
													min="1"
													value={draft.copiesPerDay}
													onChange={(e) => this.updateDraft('copiesPerDay', parseInt(e.target.value, 10) || 1)}
												/>
											</div>
										</div>
										<div className="pr-field-group compact">
											<label className="pr-field-label">Notes</label>
											<textarea
												className="pr-textarea"
												rows={3}
												value={draft.notes}
												onChange={(e) => this.updateDraft('notes', e.target.value)}
												placeholder="AV needs, dietary notes, special requirements…"
											/>
										</div>
									</div>
								)}
							</div>

							<div className="pr-modal-footer">
								<button type="button" className="pr-btn-cancel" onClick={this.closeModal}>
									Cancel
								</button>
								<button type="button" className="pr-ms-publish-btn" style={{ margin: 0 }} onClick={this.publishToAgenda}>
									<i className="las la-plus-circle" />
									Publish to Agenda
								</button>
							</div>
						</div>
					</>
				)}

				<SaveBar
					editing={editing}
					saving={saving}
					onSave={this.save}
					onCancel={this.cancel}
					updatedAt={request?.updatedAt}
				/>
			</>
		);
	}
}

export default MeetingSpaces;
